import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  IconButton,
  Paper,
  Stack,
  Switch,
  TableBody,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MailOutline,
  PersonAddOutlined,
  SyncOutlined,
  VisibilityOffOutlined,
  UndoOutlined,
  WarningAmberRounded,
} from "@mui/icons-material";
import { CeldaKit, CeldaPrincipal, Columna, FilaKit, TablaKit } from "../../components/kit/TablaKit";
import BarrasApiladas, { DatoBarra, SerieBarra } from "../../components/kit/BarrasApiladas";
import EstadoChip from "../../components/kit/EstadoChip";
import FiltroChip from "../../components/kit/FiltroChip";
import {
  useCargaEvaluadores,
  useCrearEvaluador,
  useEvaluadores,
  useNotificarEvaluadores,
  useOcultarEvaluador,
  usePeriodos,
  useSincronizarEvaluadores,
  useUpdateEvaluador,
} from "../../queries/useEvaluadores";
import { useAreas } from "../../queries/useTemas";
import {
  Evaluador,
  EvaluadorCrearInput,
  EvaluadorScope,
  Periodo,
  ResultadoNotificacion,
  ResultadoSyncEvaluadores,
} from "../../types/evaluador";
import { AreaInvestigacion } from "../../types/tema";
import { tokens } from "../../theme";
import { mensajeDeError } from "../../utils/errores";
import { Accesores, ordenar, useOrden } from "../../utils/orden";

const TODOS = "__todos__";

const JERARQUIAS = [
  { value: "ajc", label: "Académico jornada completa" },
  { value: "ajp", label: "Académico jornada parcial" },
  { value: "pex", label: "Profesor externo" },
];

const ACC_EVAL: Accesores<Evaluador> = {
  nombre: (e) => e.nombre,
  carga: (e) => e.carga,
  pendientes: (e) => e.pendientes,
  comision: (e) => e.puede_comision,
  publicar: (e) => e.puede_crear_tema,
};

// Los tres roles del gráfico, con tonos del kit (flipan solos en modo oscuro). El
// naranjo va al medio a propósito: deja los dos azules separados por un tramo de
// otro color en vez de pegados uno sobre el otro.
const SERIES_CARGA: SerieBarra[] = [
  { key: "guia", label: "Guía", color: tokens.marca.texto },
  { key: "coguia", label: "Coguía", color: tokens.naranjo[400] },
  { key: "comision", label: "Comisión", color: tokens.estado.info },
];

/**
 * Cuánta gente arrastra cada docente, de mayor a menor.
 *
 * Cuenta **estudiantes**, no procesos: el último paso que cada uno tuvo con ese
 * docente. Sin las barras en cero, que no dicen nada y estiran el eje.
 *
 * Lo manda el mismo selector que la columna «Carga» de la tabla, para que no haya dos
 * controles diciendo cosas distintas sobre lo mismo. En «Histórico» es lo que sigue
 * pendiente hoy, venga del semestre que venga; con un semestre elegido es lo que le
 * tocó ahí, cerrado o no —mirando un semestre pasado, contar solo lo abierto daría un
 * gráfico vacío, que es justo lo contrario de ver la carga que tuvo—.
 */
function GraficoCarga({ scope, periodo }: { scope?: EvaluadorScope; periodo?: Periodo }) {
  const { data = [], isLoading, isError } = useCargaEvaluadores(scope);
  const conCarga = data.filter((d) => d.total > 0);
  const datos: DatoBarra[] = conCarga.map((d) => ({
    key: d.rut,
    label: d.nombre,
    valores: { guia: d.guia, coguia: d.coguia, comision: d.comision },
  }));

  return (
    <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <Typography variant="h6">
          {periodo ? `Carga de ${periodo.ano} · ${periodo.semestre_display}` : "Carga pendiente por docente"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {periodo
            ? "Estudiantes que acompañó o evaluó ese semestre, estén cerrados o no."
            : "Estudiantes con su último paso sin cerrar, de cualquier semestre."}
        </Typography>
        {!isLoading && data.length > conCarga.length && (
          <Typography variant="caption" color="text.disabled" sx={{ ml: "auto" }}>
            {data.length - conCarga.length} docentes sin carga {periodo ? "ese semestre" : "pendiente"} (no se grafican)
          </Typography>
        )}
      </Stack>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : isError ? (
        <Alert severity="error">No se pudo cargar el gráfico.</Alert>
      ) : (
        <BarrasApiladas
          series={SERIES_CARGA}
          datos={datos}
          vacio={periodo ? "Nadie tuvo estudiantes ese semestre." : "Nadie tiene pasos pendientes."}
        />
      )}
    </Paper>
  );
}

/**
 * Un docente sin correo. Ícono propio —un sobre, en rojo— y no el triángulo de
 * `AvisoFicha`: ese cuenta lo que falta en la ficha —áreas, jerarquía— y está en casi
 * todas las filas, así que un aviso más adentro del mismo ícono no se vería. Esto es
 * poco, es rojo, y dice de qué se trata sin leer nada.
 *
 * Sin correo no se le puede escribir desde acá, no le llega el aviso de que le toca
 * evaluar, y el estudiante no tiene cómo contactarlo. El dato viene del directorio del
 * DCC, así que se arregla allá y llega con la próxima sincronización.
 */
function AvisoSinCorreo({ evaluador }: { evaluador: Evaluador }) {
  if (evaluador.email) return null;
  return (
    <Tooltip title="No tiene correo: no se le puede escribir ni avisarle que le toca evaluar. El dato viene del directorio del DCC.">
      <MailOutline sx={{ fontSize: 17, color: tokens.chip.error.fg, verticalAlign: "text-bottom", ml: 0.5 }} />
    </Tooltip>
  );
}

/**
 * Lo que le falta a la ficha de un docente para poder usarlo bien.
 *
 * Sin áreas no entra en la optimización por especialidad al armar comisiones, y sin
 * jerarquía no se sabe si es de jornada completa, parcial o externo. Las dos son
 * cosas que se arreglan entrando a su ficha, así que el aviso va en la lista —donde
 * se ven todos juntos— y dice cuál de las dos falta.
 */
function AvisoFicha({ evaluador }: { evaluador: Evaluador }) {
  const falta = [
    !evaluador.areas.length && "áreas de especialidad",
    !evaluador.jerarquia && "jerarquía",
  ].filter(Boolean) as string[];
  if (!falta.length) return null;
  return (
    <Tooltip title={`Falta ${falta.join(" y ")}. Entra a su ficha para completarlo.`}>
      <WarningAmberRounded sx={{ fontSize: 17, color: tokens.chip.aviso.fg, verticalAlign: "text-bottom", ml: 0.5 }} />
    </Tooltip>
  );
}

/**
 * Confirmación de ocultar un docente. Se insiste en que no se borra nada porque el
 * botón tiene un tacho al lado y la palabra "eliminar" en la cabeza de quien lo
 * aprieta: sus temas, comisiones y evaluaciones siguen enteros.
 */
function OcultarDialog({ docente, onClose }: { docente: Evaluador; onClose: () => void }) {
  const ocultar = useOcultarEvaluador();
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Ocultar a {docente.nombre}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary">
          <strong>No se borra su información.</strong> Sus temas, los estudiantes que guía y las evaluaciones que hizo
          quedan tal cual. Solo deja de aparecer en esta lista y no entra a comisiones nuevas.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Se puede devolver cuando quieras desde el filtro «Ocultos».
        </Typography>
        {ocultar.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {mensajeDeError(ocultar.error, "No se pudo ocultar al docente.")}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={ocultar.isPending}>
          Cancelar
        </Button>
        <Button variant="contained" color="error" disabled={ocultar.isPending} onClick={() => ocultar.mutate(docente.rut, { onSuccess: onClose })}>
          {ocultar.isPending ? "Ocultando…" : "Ocultar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** "Evaluadores" (coordinador): habilita qué docentes integran comisiones y sus áreas. */
export default function Evaluadores() {
  const [q, setQ] = useState("");
  const { data: periodos = [] } = usePeriodos();
  const [periodoKey, setPeriodoKey] = useState("");
  const [soloHabilitados, setSoloHabilitados] = useState(false);
  const { orden, onSort } = useOrden();
  const navigate = useNavigate();
  const actualizar = useUpdateEvaluador();
  const [agregar, setAgregar] = useState(false);
  const [sincronizar, setSincronizar] = useState(false);
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [correo, setCorreo] = useState(false);
  // "Ocultos" no es un filtro más: cambia a qué lista se le pregunta al servidor
  // (`?ocultos=1`), porque el catálogo normal ya viene sin ellos.
  const [verOcultos, setVerOcultos] = useState(false);
  const [ocultar, setOcultar] = useState<Evaluador | null>(null);

  // Cambiar el filtro limpia la selección: si no, quedarían marcados docentes que ya
  // no están a la vista y el correo saldría a gente que el coordinador no ve.
  useEffect(() => setSel(new Set()), [q, soloHabilitados, soloPendientes, verOcultos]);

  // Por defecto, el período más reciente (evita mostrar la carga histórica mezclada).
  useEffect(() => {
    if (!periodoKey && periodos.length) setPeriodoKey(`${periodos[0].ano}.${periodos[0].semestre}`);
  }, [periodos, periodoKey]);

  const periodoSel = periodos.find((p) => `${p.ano}.${p.semestre}` === periodoKey);
  const scope: EvaluadorScope | undefined = periodoSel ? { ano: periodoSel.ano, semestre: periodoSel.semestre } : undefined;
  const { data, isLoading, isError } = useEvaluadores(q, scope, verOcultos);
  // El contador del chip: cuántos hay al otro lado, sin cambiarse de lista.
  const { data: ocultos = [] } = useEvaluadores("", undefined, true);

  const columnas: (string | Columna)[] = [
    { label: "", width: 52 },
    { label: "Docente", sortKey: "nombre" },
    { label: "Carga", sortKey: "carga", width: 96 },
    { label: "Por revisar", sortKey: "pendientes", width: 120 },
    { label: "Comisión", sortKey: "comision", width: 120 },
    { label: "Publicar tema", sortKey: "publicar", width: 130 },
    { label: "", width: 56 },
  ];

  const todos = data ?? [];
  const nHabilitados = todos.filter((e) => e.puede_comision).length;
  const nPendientes = todos.filter((e) => e.pendientes > 0).length;
  const lista = todos.filter((e) => (!soloHabilitados || e.puede_comision) && (!soloPendientes || e.pendientes > 0));
  const evaluadores = ordenar(lista, ACC_EVAL, orden);

  // "Seleccionar todos" opera sobre `evaluadores`, o sea sobre el filtro activo.
  const visiblesSel = evaluadores.filter((e) => sel.has(e.rut)).length;
  const todosMarcados = evaluadores.length > 0 && visiblesSel === evaluadores.length;
  const alternar = (rut: number) =>
    setSel((prev) => {
      const s = new Set(prev);
      s.has(rut) ? s.delete(rut) : s.add(rut);
      return s;
    });
  const alternarTodos = () => setSel(todosMarcados ? new Set() : new Set(evaluadores.map((e) => e.rut)));

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        Evaluadores
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Habilita qué docentes pueden integrar comisiones evaluadoras, y sus áreas de especialidad.
      </Typography>

      <GraficoCarga scope={scope} periodo={periodoSel} />

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          label="Buscar"
          placeholder="Nombre o RUT…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <TextField
          select
          size="small"
          label="Carga del período"
          value={periodoKey || TODOS}
          onChange={(e) => setPeriodoKey(e.target.value === TODOS ? TODOS : e.target.value)}
          sx={{ minWidth: 190 }}
        >
          {periodos.map((p) => (
            <MenuItem key={`${p.ano}.${p.semestre}`} value={`${p.ano}.${p.semestre}`}>
              {p.ano} · {p.semestre_display}
            </MenuItem>
          ))}
          <MenuItem value={TODOS}>Histórico (todos)</MenuItem>
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" startIcon={<SyncOutlined />} onClick={() => setSincronizar(true)}>
          Sincronizar
        </Button>
        <Button variant="contained" startIcon={<PersonAddOutlined />} onClick={() => setAgregar(true)}>
          Agregar docente
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <FormControlLabel
          sx={{ mr: 0.5 }}
          control={
            <Checkbox
              size="small"
              checked={todosMarcados}
              indeterminate={visiblesSel > 0 && !todosMarcados}
              onChange={alternarTodos}
            />
          }
          label={<Typography variant="body2">Seleccionar todos</Typography>}
        />
        <FiltroChip
          label={`Habilitados para comisión (${nHabilitados})`}
          activo={soloHabilitados}
          onClick={() => setSoloHabilitados((v) => !v)}
        />
        <FiltroChip
          label={`Con revisiones pendientes (${nPendientes})`}
          activo={soloPendientes}
          onClick={() => setSoloPendientes((v) => !v)}
        />
        <FiltroChip label={`Ocultos (${ocultos.length})`} activo={verOcultos} onClick={() => setVerOcultos((v) => !v)} />
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" startIcon={<MailOutline />} disabled={sel.size === 0} onClick={() => setCorreo(true)}>
          Enviar correo{sel.size > 0 ? ` (${sel.size})` : ""}
        </Button>
      </Stack>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {isError && <Alert severity="error">No se pudieron cargar los evaluadores.</Alert>}

      {!isLoading && !isError && (
        <TablaKit columnas={columnas} minWidth={920} orden={orden} onSort={onSort}>
          <TableBody>
            {evaluadores.map((e) => (
              <FilaKit key={e.rut} destacada={sel.has(e.rut)} onClick={() => navigate(`/app/evaluadores/${e.rut}`)}>
                {/* La casilla no debe navegar al detalle (stopPropagation), igual que los toggles. */}
                <CeldaKit>
                  <Box component="span" onClick={(ev) => ev.stopPropagation()} sx={{ display: "inline-flex" }}>
                    <Checkbox size="small" checked={sel.has(e.rut)} onChange={() => alternar(e.rut)} />
                  </Box>
                </CeldaKit>
                <CeldaPrincipal>
                  {e.nombre}
                  <AvisoFicha evaluador={e} />
                  <AvisoSinCorreo evaluador={e} />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 400 }}>
                    {e.jerarquia_display || "Sin jerarquía"}
                  </Typography>
                </CeldaPrincipal>
                <CeldaKit>{e.carga}</CeldaKit>
                <CeldaKit>
                  {e.pendientes > 0 ? (
                    <EstadoChip label={String(e.pendientes)} tono="aviso" denso />
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      —
                    </Typography>
                  )}
                </CeldaKit>
                {/* Los toggles no deben navegar al detalle (stopPropagation). */}
                <CeldaKit>
                  <Box component="span" onClick={(ev) => ev.stopPropagation()} sx={{ display: "inline-flex" }}>
                    <Switch
                      checked={e.puede_comision}
                      disabled={actualizar.isPending}
                      onChange={(ev) => actualizar.mutate({ rut: e.rut, input: { puede_comision: ev.target.checked } })}
                    />
                  </Box>
                </CeldaKit>
                <CeldaKit>
                  <Box component="span" onClick={(ev) => ev.stopPropagation()} sx={{ display: "inline-flex" }}>
                    <Switch
                      checked={e.puede_crear_tema}
                      disabled={actualizar.isPending}
                      onChange={(ev) => actualizar.mutate({ rut: e.rut, input: { puede_crear_tema: ev.target.checked } })}
                    />
                  </Box>
                </CeldaKit>
                {/* Ocultar / devolver. Tampoco navega al detalle. */}
                <CeldaKit>
                  <Box component="span" onClick={(ev) => ev.stopPropagation()} sx={{ display: "inline-flex" }}>
                    {verOcultos ? (
                      <Tooltip title="Devolver a la lista">
                        <IconButton
                          size="small"
                          disabled={actualizar.isPending}
                          onClick={() => actualizar.mutate({ rut: e.rut, input: { vigente: true } })}
                        >
                          <UndoOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Ocultar (no borra su información)">
                        <IconButton size="small" onClick={() => setOcultar(e)}>
                          <VisibilityOffOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </CeldaKit>
              </FilaKit>
            ))}
          </TableBody>
        </TablaKit>
      )}

      {correo && (
        <CorreoDocentesDialog
          docentes={evaluadores.filter((e) => sel.has(e.rut))}
          onClose={() => setCorreo(false)}
          onEnviado={() => setSel(new Set())}
        />
      )}
      {agregar && <AgregarDocenteDialog onClose={() => setAgregar(false)} />}
      {sincronizar && <SincronizarDialog onClose={() => setSincronizar(false)} />}
      {ocultar && <OcultarDialog docente={ocultar} onClose={() => setOcultar(null)} />}
    </Box>
  );
}

const VACIO: EvaluadorCrearInput = {
  rut: "",
  nombre1: "",
  apellido1: "",
  email: "",
  jerarquia: "ajc",
  puede_comision: true,
  areas: [],
};

/** Sincroniza los evaluadores desde api-personas (preview → confirmar). */
function SincronizarDialog({ onClose }: { onClose: () => void }) {
  const sync = useSincronizarEvaluadores();
  const [preview, setPreview] = useState<ResultadoSyncEvaluadores | null>(null);
  const publicado = preview?.publicado;

  const correr = (commit: boolean) => sync.mutate(commit, { onSuccess: setPreview });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Sincronizar evaluadores</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Trae los evaluadores desde api-personas (grupos AJC/AJP/PEX). <strong>Solo agrega</strong>: no oculta a nadie ni
            cambia permisos de quien ya está. Los nuevos entran sin comisiones ni publicar temas, y los habilitas acá.
          </Typography>
          {sync.isError && <Alert severity="error">{mensajeDeError(sync.error, "No se pudo sincronizar.")}</Alert>}
          {preview?.avisos.map((a, i) => (
            <Alert key={i} severity="warning">
              {a}
            </Alert>
          ))}
          {preview && (
            <Box>
              {publicado ? (
                <Alert severity="success" sx={{ mb: 1 }}>
                  {preview.activos.length} evaluadores traídos del directorio.
                </Alert>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Se traerían <strong>{preview.activos.length}</strong> evaluadores. Los nuevos entran a la lista sin poder integrar
                  comisiones ni publicar temas: eso se habilita acá.
                </Typography>
              )}
              {/* Informativo, no una acción: sincronizar no oculta a nadie. */}
              {preview.fuera_del_directorio.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Ya no vienen en el directorio. No se ocultan solos; si alguno ya no tiene por qué estar, ocúltalo desde su ficha.
                  </Typography>
                  <Stack sx={{ mt: 0.5, maxHeight: 200, overflowY: "auto" }} spacing={0.25}>
                    {preview.fuera_del_directorio.map((o) => (
                      <Typography key={o.rut} variant="body2" color="text.secondary">
                        {o.nombre}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button onClick={onClose}>{publicado ? "Cerrar" : "Cancelar"}</Button>
        {!publicado && (
          <Stack direction="row" spacing={1}>
            <Button onClick={() => correr(false)} disabled={sync.isPending}>
              {sync.isPending ? "Consultando…" : "Previsualizar"}
            </Button>
            <Button variant="contained" onClick={() => correr(true)} disabled={sync.isPending || !preview}>
              Confirmar
            </Button>
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** Alta de un docente: puede guiar/crear temas y, si se habilita, integrar comisiones. */
function AgregarDocenteDialog({ onClose }: { onClose: () => void }) {
  const { data: areas = [] } = useAreas();
  const crear = useCrearEvaluador();
  const [form, setForm] = useState<EvaluadorCrearInput>(VACIO);
  const [sel, setSel] = useState<AreaInvestigacion[]>([]);

  const set = (campo: keyof EvaluadorCrearInput, valor: unknown) => setForm((prev) => ({ ...prev, [campo]: valor }));
  // Lo que el directorio tuvo que decir al registrarlo (ya existía, ya era académico…).
  // Se muestra en el mismo diálogo en vez de cerrarlo: quien lo agregó debe leerlo.
  const [aviso, setAviso] = useState<string | null>(null);

  const guardar = () =>
    crear.mutate(
      { ...form, areas: sel.map((a) => a.id) },
      { onSuccess: (data) => (data.aviso ? setAviso(data.aviso) : onClose()) },
    );

  const faltan = !form.rut.trim() || !form.nombre1.trim() || !form.apellido1.trim();

  if (aviso) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Docente agregado</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info">{aviso}</Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={onClose}>
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Agregar docente</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Se registra primero en el directorio del DCC (Factos) como profesor externo y luego acá, para que la
          sincronización lo reconozca. Podrá guiar y crear temas; habilita los interruptores para que además integre
          comisiones o evalúe. Si ya existe en el directorio, se le agrega el rol conservando sus datos.
        </Typography>
        {crear.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {mensajeDeError(crear.error, "No se pudo agregar el docente.")}
          </Alert>
        )}
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="RUT"
              placeholder="12.345.678-9"
              required
              value={form.rut}
              onChange={(e) => set("rut", e.target.value)}
              fullWidth
            />
            <TextField select label="Jerarquía" value={form.jerarquia} onChange={(e) => set("jerarquia", e.target.value)} fullWidth>
              {JERARQUIAS.map((j) => (
                <MenuItem key={j.value} value={j.value}>
                  {j.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label="Nombre" required value={form.nombre1} onChange={(e) => set("nombre1", e.target.value)} fullWidth />
            <TextField label="Segundo nombre" value={form.nombre2 ?? ""} onChange={(e) => set("nombre2", e.target.value)} fullWidth />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label="Apellido paterno" required value={form.apellido1} onChange={(e) => set("apellido1", e.target.value)} fullWidth />
            <TextField label="Apellido materno" value={form.apellido2 ?? ""} onChange={(e) => set("apellido2", e.target.value)} fullWidth />
          </Stack>
          <TextField label="Email" type="email" value={form.email ?? ""} onChange={(e) => set("email", e.target.value)} fullWidth />
          <Autocomplete
            multiple
            options={areas}
            getOptionLabel={(a) => a.nombre}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={sel}
            onChange={(_, value) => setSel(value)}
            renderInput={(params) => <TextField {...params} label="Áreas de especialidad" />}
          />
          <FormControlLabel
            control={<Switch checked={!!form.puede_comision} onChange={(e) => set("puede_comision", e.target.checked)} />}
            label="Puede integrar comisión"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={crear.isPending}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={guardar} disabled={crear.isPending || faltan}>
          {crear.isPending ? "Agregando…" : "Agregar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


/**
 * Correo del coordinador a los docentes marcados en la tabla.
 *
 * "Agregar sus revisiones pendientes" es lo que convierte este mensaje en el
 * recordatorio de evaluaciones: cada docente recibe, además del texto, la lista de
 * lo que tiene sin evaluar. Los correos se resuelven en el servidor a partir del RUT.
 */
function CorreoDocentesDialog({
  docentes,
  onClose,
  onEnviado,
}: {
  docentes: Evaluador[];
  onClose: () => void;
  onEnviado: () => void;
}) {
  const notificar = useNotificarEvaluadores();
  const [asunto, setAsunto] = useState("");
  const [cuerpo, setCuerpo] = useState("");
  const [incluirPendientes, setIncluirPendientes] = useState(false);
  const [resultado, setResultado] = useState<ResultadoNotificacion | null>(null);

  const conPendientes = docentes.filter((d) => d.pendientes > 0).length;

  const enviar = () =>
    notificar.mutate(
      { ruts: docentes.map((d) => d.rut), asunto: asunto.trim(), cuerpo: cuerpo.trim(), incluir_pendientes: incluirPendientes },
      { onSuccess: setResultado },
    );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enviar correo a {docentes.length} docente(s)</DialogTitle>
      <DialogContent dividers>
        {resultado ? (
          <Stack spacing={1.5}>
            <Alert severity="success">Se enviaron {resultado.enviados} correo(s).</Alert>
            {resultado.fallidos > 0 && <Alert severity="error">{resultado.fallidos} no se pudieron enviar.</Alert>}
            {resultado.sin_correo.length > 0 && (
              <Alert severity="warning">Sin dirección de correo: {resultado.sin_correo.join(", ")}.</Alert>
            )}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              El mensaje sale con la cabecera y el pie del sistema. Los docentes sin correo cargado se informan al final.
            </Typography>
            <TextField label="Asunto" value={asunto} onChange={(e) => setAsunto(e.target.value)} fullWidth required />
            <TextField
              label="Mensaje"
              value={cuerpo}
              onChange={(e) => setCuerpo(e.target.value)}
              fullWidth
              multiline
              minRows={6}
              required
            />
            <FormControlLabel
              control={<Checkbox checked={incluirPendientes} onChange={(e) => setIncluirPendientes(e.target.checked)} />}
              label={
                <Typography variant="body2">
                  Agregar a cada uno sus revisiones pendientes
                  {conPendientes > 0 ? ` (${conPendientes} de los elegidos tiene)` : " (ninguno de los elegidos tiene)"}
                </Typography>
              }
            />
            {notificar.isError && <Alert severity="error">{mensajeDeError(notificar.error, "No se pudo enviar el correo.")}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            if (resultado) onEnviado();
            onClose();
          }}
        >
          {resultado ? "Cerrar" : "Cancelar"}
        </Button>
        {!resultado && (
          <Button variant="contained" disabled={notificar.isPending || !asunto.trim() || !cuerpo.trim()} onClick={enviar}>
            {notificar.isPending ? "Enviando…" : "Enviar"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
